import { supabase } from '../supabase/client';
import { ServiceRequest } from '../../types';
import { BlacklistGuard } from './blacklistGuard';
import { ActivityService } from './activityService';
import { MixpanelAnalytics } from '../../analytics';
import { MarketplaceAnalytics } from '../../analytics/marketplaceEvents';

/**
 * 建立服務請求（獨立模組，避免 ServiceRequestService 大包初始化失敗）
 */
export async function createServiceRequest(
  requestData: Partial<ServiceRequest>
): Promise<{ request: ServiceRequest | null; error: string | null }> {
  try {
    if (!requestData.userId || requestData.userId === 'temp-user-id') {
      return { request: null, error: '無效的用戶ID，請重新登入' };
    }

    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(requestData.userId)) {
      return { request: null, error: '用戶ID格式無效，請重新登入' };
    }

    await BlacklistGuard.assertUserAllowed(requestData.userId);

    const { data, error } = await supabase
      .from('service_requests')
      .insert({
        user_id: requestData.userId,
        category_id: requestData.categoryId,
        title: requestData.title,
        description: requestData.description,
        location: requestData.location,
        address: requestData.address,
        scheduled_date: requestData.scheduledDate,
        scheduled_time: requestData.scheduledTime,
        urgency: requestData.urgency,
        estimated_price: requestData.estimatedPrice,
        estimated_hours: requestData.estimatedHours,
        pricing_mode: requestData.pricingMode ?? 'hourly',
        fixed_total_price: requestData.fixedTotalPrice ?? null,
        price_negotiable: requestData.priceNegotiable,
        craftsman_id: requestData.craftsmanId,
        company_id: requestData.companyId,
        status: 'pending',
        photos: requestData.photos || [],
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Database error creating service request:', error);
      return { request: null, error: error.message };
    }

    const createdRequest: ServiceRequest = {
      id: data.id,
      categoryId: data.category_id,
      title: data.title,
      description: data.description,
      location: data.location,
      address: data.address,
      scheduledDate: data.scheduled_date,
      scheduledTime: data.scheduled_time,
      urgency: data.urgency,
      estimatedPrice: data.estimated_price,
      estimatedHours: data.estimated_hours,
      pricingMode: data.pricing_mode ?? 'hourly',
      fixedTotalPrice: data.fixed_total_price,
      priceNegotiable: data.price_negotiable,
      craftsmanId: data.craftsman_id,
      companyId: data.company_id,
      status: data.status,
      photos: data.photos || [],
      createdAt: new Date(data.created_at),
      userId: data.user_id,
    };

    try {
      await ActivityService.logActivity({
        userId: requestData.userId,
        activityType: 'service_request_created',
        description: `創建服務請求：${data.title}`,
        metadata: {
          serviceRequestId: data.id,
          categoryId: data.category_id,
          craftsmanId: data.craftsman_id,
          companyId: data.company_id,
        },
        status: 'success',
      });
    } catch (activityError) {
      console.warn('記錄服務請求創建活動失敗:', activityError);
    }

    MixpanelAnalytics.projectCreated({
      project_id: data.id,
      project_type: 'service_request',
      category_id: data.category_id,
      pricing_mode: data.pricing_mode ?? 'hourly',
    });

    MarketplaceAnalytics.serviceRequestCreated({
      request_id: data.id,
      category_id: data.category_id,
      pricing_mode: data.pricing_mode ?? 'hourly',
      is_urgent: data.urgency === 'urgent' || data.urgency === 'emergency',
      has_photos: Array.isArray(data.photos) && data.photos.length > 0,
    });

    try {
      const { notifyProvidersForEstablishedServiceRequest } = await import(
        './serviceRequestProviderNotifications'
      );
      await notifyProvidersForEstablishedServiceRequest({
        serviceRequestId: data.id,
        title: data.title,
        craftsmanId: data.craftsman_id,
        companyId: data.company_id,
        pricingMode: data.pricing_mode,
        estimatedPrice: data.estimated_price,
      });
    } catch (notifyError) {
      console.error('創建服務請求後發送通知失敗:', notifyError);
    }

    return { request: createdRequest, error: null };
  } catch (error) {
    console.error('Error creating service request:', error);
    return { request: null, error: '創建服務請求失敗' };
  }
}
